import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
    ArrowPathIcon,
    ArrowTopRightOnSquareIcon,
    ExclamationTriangleIcon,
    InformationCircleIcon,
    UsersIcon,
    MapPinIcon,
    ArrowDownTrayIcon,
    TrashIcon,
} from "@heroicons/react/24/outline";
import getAnalytics from "../api/getAnalytics";
import getGeoAnalytics from "../api/getGeoAnalytics";
import AnalyticsGlobe, { REGION_ID_TO_FEATURE } from "../components/AnalyticsGlobe";
import { useTheme } from "../contexts/ThemeContext";

const GA_URL =
    "https://analytics.google.com/analytics/web/?authuser=0&hl=en-GB&fpn=725602347262&origin=https%3A%2F%2Fconsole.firebase.google.com#/p456848856/reports/dashboard?r=firebase-overview&fpn=725602347262";

const eventTranslations = {
    user_engagement: "Активность пользователя",
    screen_view: "Просмотр экрана",
    notification_receive: "Получение уведомления",
    session_start: "Начало сессии",
    notification_foreground: "Уведомление на переднем плане",
    first_open: "Первое открытие",
    notification_dismiss: "Отклонение уведомления",
    app_remove: "Удаление приложения",
    notification_open: "Открытие уведомления",
    app_update: "Обновление приложения",
    app_clear_data: "Очистка данных",
    os_update: "Обновление системы",
    announcement_view: "Просмотр объявления",
    category_selection: "Выбор категории",
};

const eventDescriptions = {
    user_engagement: "Показывает, насколько активно пользователь использует приложение.",
    screen_view: "Количество событий просмотра экранов в приложении.",
    notification_receive: "Количество полученных уведомлений на устройстве пользователя.",
    session_start: "Количество запусков приложения или начала активной сессии.",
    notification_foreground: "Уведомления, полученные, когда приложение находится на переднем плане.",
    first_open: "Количество пользователей, которые впервые открыли приложение.",
    notification_dismiss: "Количество случаев, когда уведомление было отклонено или закрыто.",
    app_remove: "Количество пользователей, которые удалили приложение со своего устройства.",
    notification_open: "Количество случаев открытия уведомления пользователем.",
    app_update: "Количество обновлений приложения до новой версии.",
    app_clear_data: "Количество случаев очистки данных приложения пользователем.",
    os_update: "Количество обновлений операционной системы устройства.",
    announcement_view: "Количество просмотров объявлений пользователями в приложении.",
    category_selection: "Событие выбора пользователем определенной категории в приложении.",
};

const GEO_METRICS = [
    { key: "users", label: "Пользователи" },
    { key: "announcements", label: "Объявления" },
    { key: "views", label: "Просмотры" },
];

const fmt = (n) => Number(n || 0).toLocaleString("ru-RU");
const pct = (part, total) => (total > 0 ? `${((part / total) * 100).toFixed(1).replace(".0", "")}%` : "—");

const Card = ({ className = "", children }) => (
    <section className={`rounded-block border border-border-primary bg-bg-card shadow-sm ${className}`}>{children}</section>
);

const CardHeader = ({ title, subtitle, children }) => (
    <div className="flex flex-wrap items-center justify-between gap-3 border-b border-border-primary px-5 py-4">
        <div className="min-w-0">
            <h3 className="text-base font-semibold text-text-primary">{title}</h3>
            {subtitle && <p className="mt-0.5 text-xs text-text-secondary">{subtitle}</p>}
        </div>
        {children}
    </div>
);

const ErrorState = ({ text, onRetry }) => (
    <div className="flex h-full min-h-[200px] flex-col items-center justify-center gap-3 px-6 py-10 text-center">
        <ExclamationTriangleIcon className="h-10 w-10 text-rose-500/80" />
        <p className="text-sm text-text-secondary">{text}</p>
        <button
            type="button"
            onClick={onRetry}
            className="inline-flex items-center gap-2 rounded-lg border border-border-primary px-3 py-1.5 text-sm font-medium text-text-secondary hover:bg-bg-tertiary"
        >
            <ArrowPathIcon className="h-4 w-4" />
            Повторить
        </button>
    </div>
);

const KpiCard = ({ icon: Icon, label, value, hint, tone, loading }) => (
    <Card className="p-4">
        <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
                <p className="text-xs font-medium text-text-secondary">{label}</p>
                {loading ? (
                    <div className="mt-2 h-7 w-24 animate-pulse rounded bg-bg-tertiary" />
                ) : (
                    <p className="mt-1 text-2xl font-bold tabular-nums text-text-primary">{value}</p>
                )}
                {hint && !loading && <p className="mt-1 truncate text-xs text-text-muted">{hint}</p>}
            </div>
            <span className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-lg ${tone}`}>
                <Icon className="h-5 w-5" />
            </span>
        </div>
    </Card>
);

function Analytics() {
    const [events, setEvents] = useState([]);
    const [eventsState, setEventsState] = useState("loading"); // loading | ready | error
    const [geoData, setGeoData] = useState([]);
    const [geoTotals, setGeoTotals] = useState(null);
    const [geoState, setGeoState] = useState("loading");
    const [geoMetric, setGeoMetric] = useState("users");
    const [highlightFeature, setHighlightFeature] = useState(-1);
    const [openEvent, setOpenEvent] = useState(null);
    const { theme } = useTheme();

    const fetchEvents = useCallback(async () => {
        setEventsState("loading");
        try {
            const response = await getAnalytics();
            const list = (response.analytics || [])
                .map((item) => ({
                    key: item.eventName,
                    name: eventTranslations[item.eventName] || item.eventName,
                    description: eventDescriptions[item.eventName],
                    count: Number(item.eventCount) || 0,
                }))
                .sort((a, b) => b.count - a.count);
            setEvents(list);
            setEventsState("ready");
        } catch (error) {
            console.error("Ошибка при получении данных:", error);
            setEventsState("error");
        }
    }, []);

    const fetchGeo = useCallback(async () => {
        setGeoState("loading");
        try {
            const response = await getGeoAnalytics();
            setGeoData(response.regions || []);
            setGeoTotals(response.totals || null);
            setGeoState("ready");
        } catch (error) {
            console.error("Ошибка при получении гео-аналитики:", error);
            setGeoState("error");
        }
    }, []);

    useEffect(() => {
        fetchEvents();
        fetchGeo();
    }, [fetchEvents, fetchGeo]);

    const refreshAll = () => {
        fetchEvents();
        fetchGeo();
    };

    const handleGlobeSelect = useCallback((featureIdx) => {
        setHighlightFeature((prev) => (prev === featureIdx ? -1 : featureIdx));
    }, []);

    const eventCount = useCallback((key) => events.find((e) => e.key === key)?.count || 0, [events]);
    const eventsMax = events[0]?.count || 1;
    const eventsTotal = useMemo(() => events.reduce((s, e) => s + e.count, 0), [events]);

    const regions = useMemo(
        () =>
            [...geoData]
                .map((r) => ({ ...r, value: Number(r[geoMetric]) || 0 }))
                .sort((a, b) => b.value - a.value),
        [geoData, geoMetric]
    );
    const topRegions = regions.slice(0, 10);
    const regionsTotal = regions.reduce((s, r) => s + r.value, 0);
    const topRegionMax = Math.max(topRegions[0]?.value || 0, 1);

    const usersActive = Number(geoTotals?.users_active || 0);
    const usersWithRegion = Number(geoTotals?.users_with_region || 0);
    const loadingAny = eventsState === "loading" || geoState === "loading";

    return (
        <div className="space-y-4 text-left">
            {/* Header */}
            <div className="flex flex-wrap items-end justify-between gap-3">
                <div>
                    <h2 className="text-2xl font-bold text-text-primary">Аналитика</h2>
                    <p className="mt-0.5 text-sm text-text-secondary">
                        Пользователи по регионам и события мобильного приложения
                    </p>
                </div>
                <div className="flex gap-2">
                    <button
                        type="button"
                        onClick={refreshAll}
                        disabled={loadingAny}
                        className="inline-flex items-center gap-2 rounded-lg border border-border-primary px-3 py-2 text-sm font-medium text-text-secondary hover:bg-bg-tertiary disabled:opacity-50"
                    >
                        <ArrowPathIcon className={`h-4 w-4 ${loadingAny ? "animate-spin" : ""}`} />
                        Обновить
                    </button>
                    <a
                        href={GA_URL}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-2 rounded-lg bg-accent-grad px-3 py-2 text-sm font-semibold text-white hover:opacity-90"
                    >
                        Google Analytics
                        <ArrowTopRightOnSquareIcon className="h-4 w-4" />
                    </a>
                </div>
            </div>

            {/* KPI */}
            <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
                <KpiCard
                    icon={UsersIcon}
                    label="Активные пользователи"
                    value={geoState === "error" ? "—" : fmt(usersActive)}
                    hint="Всего в базе"
                    tone="bg-indigo-500/15 text-indigo-500"
                    loading={geoState === "loading"}
                />
                <KpiCard
                    icon={MapPinIcon}
                    label="Указали регион"
                    value={geoState === "error" ? "—" : pct(usersWithRegion, usersActive)}
                    hint={geoState === "ready" ? `${fmt(usersWithRegion)} из ${fmt(usersActive)}` : null}
                    tone="bg-sky-500/15 text-sky-500"
                    loading={geoState === "loading"}
                />
                <KpiCard
                    icon={ArrowDownTrayIcon}
                    label="Новые установки"
                    value={eventsState === "error" ? "—" : fmt(eventCount("first_open"))}
                    hint="Первое открытие · 30 дней"
                    tone="bg-emerald-500/15 text-emerald-500"
                    loading={eventsState === "loading"}
                />
                <KpiCard
                    icon={TrashIcon}
                    label="Удаления"
                    value={eventsState === "error" ? "—" : fmt(eventCount("app_remove"))}
                    hint={
                        eventsState === "ready" && eventCount("first_open") > 0
                            ? `${pct(eventCount("app_remove"), eventCount("first_open"))} от установок · 30 дней`
                            : "30 дней"
                    }
                    tone="bg-rose-500/15 text-rose-500"
                    loading={eventsState === "loading"}
                />
            </div>

            {/* Geo */}
            <Card>
                <CardHeader title="География" subtitle="Нажмите на регион в списке или на карте, чтобы выделить его">
                    <div role="tablist" className="flex rounded-lg border border-border-primary bg-bg-primary p-0.5">
                        {GEO_METRICS.map(({ key, label }) => (
                            <button
                                key={key}
                                type="button"
                                role="tab"
                                aria-selected={geoMetric === key}
                                onClick={() => setGeoMetric(key)}
                                className={`rounded-md px-3 py-1.5 text-xs font-semibold transition-colors ${
                                    geoMetric === key
                                        ? "bg-accent-grad text-white shadow-sm"
                                        : "text-text-secondary hover:text-text-primary"
                                }`}
                            >
                                {label}
                            </button>
                        ))}
                    </div>
                </CardHeader>

                {geoState === "error" ? (
                    <ErrorState text="Не удалось загрузить данные по регионам" onRetry={fetchGeo} />
                ) : (
                    <div className="grid grid-cols-1 lg:grid-cols-5">
                        <div className="h-[360px] border-b border-border-primary lg:col-span-3 lg:h-[440px] lg:border-b-0 lg:border-r">
                            {geoState === "loading" ? (
                                <div className="flex h-full items-center justify-center">
                                    <div className="h-48 w-48 animate-pulse rounded-full bg-bg-tertiary" />
                                </div>
                            ) : (
                                <AnalyticsGlobe
                                    data={geoData}
                                    metric={geoMetric}
                                    dark={theme === "dark"}
                                    highlight={highlightFeature}
                                    onSelect={handleGlobeSelect}
                                />
                            )}
                        </div>

                        <div className="p-4 lg:col-span-2">
                            <div className="mb-2 flex items-baseline justify-between px-2">
                                <span className="text-sm font-semibold text-text-primary">Топ-10 регионов</span>
                                {geoState === "ready" && (
                                    <span className="text-xs text-text-muted">всего {fmt(regionsTotal)}</span>
                                )}
                            </div>
                            <ol className="space-y-0.5">
                                {geoState === "loading" &&
                                    Array.from({ length: 8 }).map((_, i) => (
                                        <li key={i} className="animate-pulse space-y-2 px-2 py-2.5">
                                            <div className="h-3 w-2/3 rounded bg-bg-tertiary" />
                                            <div className="h-1.5 rounded bg-bg-tertiary" />
                                        </li>
                                    ))}
                                {geoState === "ready" && topRegions.length === 0 && (
                                    <li className="px-2 py-8 text-center text-sm text-text-muted">Нет данных</li>
                                )}
                                {geoState === "ready" &&
                                    topRegions.map((region, i) => {
                                        const feature = REGION_ID_TO_FEATURE[region.region_id];
                                        const selectable = feature !== undefined;
                                        const isHighlighted = selectable && feature === highlightFeature;
                                        return (
                                            <li key={region.region_id}>
                                                <button
                                                    type="button"
                                                    disabled={!selectable}
                                                    onClick={() => handleGlobeSelect(feature)}
                                                    aria-pressed={isHighlighted}
                                                    className={`w-full rounded-lg px-2 py-2 text-left transition-colors ${
                                                        isHighlighted
                                                            ? "bg-[#4F45E5]/15"
                                                            : selectable
                                                            ? "hover:bg-bg-tertiary"
                                                            : "cursor-default"
                                                    }`}
                                                >
                                                    <div className="mb-1.5 flex items-center gap-2">
                                                        <span
                                                            className={`w-5 shrink-0 text-center text-xs font-semibold tabular-nums ${
                                                                i < 3 ? "text-[#7C3AED]" : "text-text-muted"
                                                            }`}
                                                        >
                                                            {i + 1}
                                                        </span>
                                                        <span className="min-w-0 flex-1 truncate text-sm font-medium text-text-primary">
                                                            {region.name_ru || region.name_kz}
                                                        </span>
                                                        <span className="shrink-0 text-sm font-semibold tabular-nums text-text-primary">
                                                            {fmt(region.value)}
                                                        </span>
                                                        <span className="w-11 shrink-0 text-right text-xs tabular-nums text-text-muted">
                                                            {pct(region.value, regionsTotal)}
                                                        </span>
                                                    </div>
                                                    <div className="ml-7 h-1.5 overflow-hidden rounded-full bg-bg-tertiary">
                                                        <div
                                                            className="h-full rounded-full bg-accent-grad transition-all duration-500"
                                                            style={{
                                                                width: `${Math.max(
                                                                    (region.value / topRegionMax) * 100,
                                                                    region.value > 0 ? 2 : 0
                                                                )}%`,
                                                            }}
                                                        />
                                                    </div>
                                                </button>
                                            </li>
                                        );
                                    })}
                            </ol>
                        </div>
                    </div>
                )}
            </Card>

            {/* Events */}
            <Card>
                <CardHeader
                    title="События приложения"
                    subtitle={
                        eventsState === "ready"
                            ? `Firebase / GA4 · последние 30 дней · всего ${fmt(eventsTotal)}`
                            : "Firebase / GA4 · последние 30 дней"
                    }
                />
                {eventsState === "error" ? (
                    <ErrorState text="Не удалось загрузить события из Google Analytics" onRetry={fetchEvents} />
                ) : (
                    <ul className="divide-y divide-border-primary">
                        {eventsState === "loading" &&
                            Array.from({ length: 8 }).map((_, i) => (
                                <li key={i} className="flex animate-pulse items-center gap-4 px-5 py-3">
                                    <div className="h-3 w-40 rounded bg-bg-tertiary" />
                                    <div className="h-2 flex-1 rounded bg-bg-tertiary" />
                                    <div className="h-3 w-14 rounded bg-bg-tertiary" />
                                </li>
                            ))}
                        {eventsState === "ready" && events.length === 0 && (
                            <li className="px-5 py-10 text-center text-sm text-text-muted">Нет событий за период</li>
                        )}
                        {eventsState === "ready" &&
                            events.map((ev) => {
                                const open = openEvent === ev.key;
                                return (
                                    <li key={ev.key}>
                                        <button
                                            type="button"
                                            onClick={() => setOpenEvent(open ? null : ev.key)}
                                            aria-expanded={open}
                                            className="group grid w-full grid-cols-[minmax(0,1fr)_auto] items-center gap-x-4 gap-y-1.5 px-5 py-3 text-left hover:bg-bg-tertiary/60 sm:grid-cols-[14rem_minmax(0,1fr)_6rem]"
                                        >
                                            <span className="flex min-w-0 items-center gap-1.5">
                                                <span className="truncate text-sm font-medium text-text-primary" title={ev.key}>
                                                    {ev.name}
                                                </span>
                                                <InformationCircleIcon
                                                    className={`h-4 w-4 shrink-0 transition-colors ${
                                                        open ? "text-[#7C3AED]" : "text-text-muted opacity-0 group-hover:opacity-100"
                                                    }`}
                                                />
                                            </span>
                                            <span className="col-span-2 row-start-2 h-2 overflow-hidden rounded-full bg-bg-tertiary sm:col-span-1 sm:row-start-auto">
                                                <span
                                                    className="block h-full rounded-full bg-accent-grad transition-all duration-500"
                                                    style={{ width: `${Math.max((ev.count / eventsMax) * 100, ev.count > 0 ? 1 : 0)}%` }}
                                                />
                                            </span>
                                            <span className="col-start-2 row-start-1 text-right text-sm font-semibold tabular-nums text-text-primary sm:col-start-auto">
                                                {fmt(ev.count)}
                                            </span>
                                            {open && (
                                                <span className="col-span-2 text-xs text-text-secondary sm:col-span-3">
                                                    {ev.description || "Описание отсутствует"}{" "}
                                                    <code className="rounded bg-bg-tertiary px-1 py-0.5 text-[11px] text-text-muted">{ev.key}</code>
                                                </span>
                                            )}
                                        </button>
                                    </li>
                                );
                            })}
                    </ul>
                )}
            </Card>
        </div>
    );
}

export default Analytics;
